import React, { useState } from 'react';
import styles from './ReviewsTable.module.scss';
import CustomTable from '../../../Common/CustomTable';
import clx from 'classnames';
import { useDispatch } from 'react-redux';
import { reviewsActions } from '../../store';
import { useTranslation } from 'react-i18next';
import { Rating } from '@mui/material';
import { Button } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import HoveredInfoIcon from 'Containers/Common/HoveredInfoIcon';
import ShareReviewStoryButton from './ShareReviewStoryButton';
import ConfirmSecondaryModal from 'Containers/Common/ConfirmSecondaryModal';
import { ReactComponent as WarningIcon } from 'assets/svg/icons/warning_user.svg';

dayjs.locale('it');

const StarRating = ({ value, readOnly = true, size = 'small', color = '#000' }) => {
  return (
    <Rating
      value={value}
      readOnly={readOnly}
      size={size}
      precision={0.1}
      sx={{
        '& .MuiRating-iconFilled': {
          color: color,
        },
        '& .MuiRating-iconEmpty': {
          color: color,
        },
      }}
    />
  );
};

export default ({ reviews, reviewsStats, isMobile }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [reviewToReport, setReviewToReport] = useState(null);

  const handleConfirmReport = () => {
    if (!reviewToReport) return;
    dispatch(
      reviewsActions.REPORT_REVIEW.REQUEST({
        idReview: reviewToReport,
      }),
    );
    setReviewToReport(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const getReviewerName = (review) => {
    if (review.nome && review.cognome) {
      return `${review.nome} ${review.cognome}`;
    }
    return review.nome || review.cognome || '-';
  };

  const columns = React.useMemo(
    () =>
      isMobile
        ? [
            {
              Header: '',
              accessor: 'review',
              Cell: ({ row: { original } }) => {
                const isReported = original?.segnalata === 1;
                const isHidden = original.hide === 1;
                return (
                  <div className={styles.review_mobile_card}>
                    <div className={styles.review_header_mobile}>
                      <div className={styles.rating_mobile}>
                        <StarRating value={original.rating_average} size="small" />
                        <span className={styles.rating_value_mobile}>
                          {original.rating_average?.toFixed(1) || '0.0'}
                        </span>
                        <HoveredInfoIcon
                          description={
                            <>
                              Valutazione media del tuo salone.
                              <br /><br /> 
                              Salone: <br /> 
                                <StarRating value={original.rating_salone} size="small" color="#fff" /> {original.rating_salone?.toFixed(1) || '0.0'} 
                              <br /><br /> 
                              Servizio: <br />
                                <StarRating value={original.rating_servizio} size="small" color="#fff" /> {original.rating_servizio?.toFixed(1) || '0.0'} 
                              <br /><br />  
                              Personale: <br />
                                <StarRating value={original.rating_personale} size="small" color="#fff" /> {original.rating_personale?.toFixed(1) || '0.0'} 
                            </>
                          }
                          sidePosition="right"
                        />
                      </div>
                      <div className={styles.header_right_mobile}>
                        <div className={styles.date_mobile}>
                          {formatDate(original.created_at)}
                        </div>
                        {!isHidden && !isReported ? (
                          <button
                            type="button"
                            className={styles.report_link_mobile}
                            onClick={() => setReviewToReport(original.id_recensione)}
                          >
                            {t('reviews.table.reportReview')}
                          </button>
                        ) : isHidden ? (
                          <span className={styles.hidden}>
                            {t('reviews.table.hidden')}
                          </span>
                        ) : (
                          <span className={styles.reported}>
                            {t('reviews.table.reported')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={styles.reviewer_mobile}>
                      {getReviewerName(original)}
                    </div>
                    {original.testo && (
                      <div className={styles.review_text_mobile}>
                        {original.testo}
                      </div>
                    )}
                    <div className={styles.share_row_mobile}>
                      <ShareReviewStoryButton review={original} />
                    </div>
                  </div>
                );
              },
            },
          ]
        : [
            {
              Header: t('reviews.table.rating'),
              accessor: 'rating_average',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.rating_cell}>
                    <StarRating value={original.rating_average} size="medium" />
                    <span className={styles.rating_value}>
                      {original.rating_average?.toFixed(1) || '0.0'}
                    </span>
                    <HoveredInfoIcon
                      description={
                        <>
                          Valutazione media del tuo salone.
                          <br /><br /> 
                          Salone: <br /> 
                            <StarRating value={original.rating_salone} size="small" color="#fff" /> {original.rating_salone?.toFixed(1) || '0.0'} 
                          <br /><br /> 
                          Servizio: <br />
                            <StarRating value={original.rating_servizio} size="small" color="#fff" /> {original.rating_servizio?.toFixed(1) || '0.0'} 
                          <br /><br />  
                          Personale: <br />
                            <StarRating value={original.rating_personale} size="small" color="#fff" /> {original.rating_personale?.toFixed(1) || '0.0'} 
                        </>
                      }
                      sidePosition="right"
                    />
                  </div>
                );
              },
            },
            {
              Header: t('reviews.table.review'),
              accessor: 'testo',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.text_cell}>
                    {original.testo || '-'}
                  </div>
                );
              },
            },
            {
              Header: t('reviews.table.reviewer'),
              accessor: 'reviewer',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.reviewer_cell}>
                    <div>{getReviewerName(original)}</div>
                    <div className={styles.date}>{formatDate(original.created_at)}</div>
                  </div>
                );
              },
            },
            {
              Header: '',
              accessor: 'share',
              Cell: ({ row: { original } }) => (
                <div className={styles.share_cell}>
                  <ShareReviewStoryButton review={original} />
                </div>
              ),
            },
            {
              Header: '',
              accessor: 'report',
              Cell: ({ row: { original } }) => {
                const isReported = original?.segnalata === 1;
                const isHidden = original.hide === 1;
                return (
                  <div className={styles.report_cell}>
                    {!isHidden && !isReported ? (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setReviewToReport(original.id_recensione)}
                        className={styles.report_button}
                      >
                        {t('reviews.table.reportReview')}
                      </Button>
                    ): isHidden ? (
                      <div className={styles.hidden}>
                        {t('reviews.table.hidden')}
                      </div>
                    ) : (
                      <div className={styles.reported}>
                        {t('reviews.table.reported')}
                      </div>
                    )}
                  </div>
                );
              },
            },
          ],
    [isMobile, t],
  );

  return (
    <div
      className={clx(styles.table_card, {
        [styles.table_card_mobile]: isMobile,
      })}
    >
      <ConfirmSecondaryModal
        isModalOpen={!!reviewToReport}
        setIsModalOpen={(open) => {
          if (!open) setReviewToReport(null);
        }}
        Icon={WarningIcon}
        title={t('reviews.confirmReport.title')}
        subtitle={t('reviews.confirmReport.subtitle')}
        buttons={[t('reviews.table.reportReview')]}
        buttonsActions={[handleConfirmReport]}
      />
      <CustomTable data={reviews} columns={columns} isMobile={isMobile} />
    </div>
  );
};
